import { FC, useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { Box, Button, Modal, ModalBody, ModalCloseButton, ModalContent, ModalFooter, ModalHeader, ModalOverlay, rem, Text } from '@bonusly/design-system';

import { DoodleCanvas } from './doodle-canvas';
import { DoodleToolbar } from './doodle-toolbar';
import { canvasToBlob, renderStrokeToCtx } from './stroke-utils';
import { useDoodle } from './use-doodle';

interface DoodleModalProps {
	isOpen: boolean;
	onClose: () => void;
	onSave: (file: File) => void | Promise<void>;
}

export const DoodleModal: FC<DoodleModalProps> = ({ isOpen, onClose, onSave }) => {
	const { t } = useTranslation('action_input');
	const {
		tool,
		setTool,
		color,
		setColor,
		size,
		setSize,
		strokes,
		currentStroke,
		startStroke,
		addPoint,
		endStroke,
		performFill,
		undo,
		redo,
		clear,
		canUndo,
		canRedo,
		hasContent,
	} = useDoodle();
	const isSaving = useRef(false);
	const strokesRef = useRef(strokes);
	strokesRef.current = strokes;

	const handleSave = useCallback(async () => {
		if (isSaving.current || !strokesRef.current.length) return;
		isSaving.current = true;

		try {
			const exportCanvas = document.createElement('canvas');
			exportCanvas.width = 800;
			exportCanvas.height = 500;
			const ctx = exportCanvas.getContext('2d');
			if (!ctx) return;

			ctx.fillStyle = '#ffffff';
			ctx.fillRect(0, 0, 800, 500);

			for (const stroke of strokesRef.current) {
				renderStrokeToCtx(ctx, stroke);
			}

			const blob = await canvasToBlob(exportCanvas);
			const file = new File([blob], `doodle-${Date.now()}.png`, { type: 'image/png' });
			await onSave(file);
			clear();
			onClose();
		} finally {
			isSaving.current = false;
		}
	}, [onSave, onClose, clear]);

	const handleClose = useCallback(() => {
		clear();
		onClose();
	}, [clear, onClose]);

	useEffect(() => {
		if (!isOpen) return;

		const handleKeyDown = (e: KeyboardEvent) => {
			const isMod = e.metaKey || e.ctrlKey;

			if (isMod && e.key === 'z' && !e.shiftKey) {
				e.preventDefault();
				undo();
				return;
			}
			if (isMod && ((e.key === 'z' && e.shiftKey) || e.key === 'y')) {
				e.preventDefault();
				redo();
				return;
			}

			if (isMod || e.altKey) return;

			switch (e.key.toLowerCase()) {
				case 'b':
					setTool('brush');
					break;
				case 'e':
					setTool('eraser');
					break;
				case 'f':
					setTool('fill');
					break;
			}
		};

		window.addEventListener('keydown', handleKeyDown);
		return () => window.removeEventListener('keydown', handleKeyDown);
	}, [isOpen, undo, redo, setTool]);

	return (
		<Modal isOpen={isOpen} onClose={handleClose} size="4xl" closeOnOverlayClick={false}>
			<ModalOverlay />
			<ModalContent maxW={{ base: '95vw', md: '900px' }} mx="auto">
				<ModalHeader borderBottomWidth={rem(1)} borderColor="border.default" borderStyle="solid" color="text.default">
					<Text fontSize="lg" fontWeight="semibold">
						{t('doodle.title')}
					</Text>
				</ModalHeader>
				<ModalCloseButton size="lg" borderRadius="full" />

				<ModalBody py={4} px={{ base: 3, sm: 6 }}>
					<Box mb={4}>
						<DoodleToolbar
							tool={tool}
							color={color}
							size={size}
							canUndo={canUndo}
							canRedo={canRedo}
							onToolChange={setTool}
							onColorChange={setColor}
							onSizeChange={setSize}
							onUndo={undo}
							onRedo={redo}
							onClear={clear}
						/>
					</Box>

					<DoodleCanvas
						tool={tool}
						strokes={strokes}
						currentStroke={currentStroke}
						onStartStroke={startStroke}
						onAddPoint={addPoint}
						onEndStroke={endStroke}
						onFill={performFill}
					/>
				</ModalBody>

				<ModalFooter gap={3} pt={1} pb={4}>
					<Button variant="ghost" colorScheme="secondary" onClick={handleClose}>
						{t('doodle.cancel')}
					</Button>
					<Button colorScheme="primary" onClick={handleSave} isDisabled={!hasContent}>
						{t('doodle.done')}
					</Button>
				</ModalFooter>
			</ModalContent>
		</Modal>
	);
};
